import axios from "axios";
import { ApiUrlConstant } from "@constants/apiUrls";
import type { GatewayConfiguration } from "@constants/verticalConstants";

function buildGatewayHeaders(gateway: GatewayConfiguration) {
  return {
    "product-id": String(gateway.productId),
    "tenant-id": String(gateway.tenantId),
    platform: "web",
  };
}

export async function getLoginDto(
  accessToken: string,
  gateway: GatewayConfiguration
) {
  try {
    const api = axios.create({
      baseURL: ApiUrlConstant.gateWayUrl,
      headers: {
        "Content-Type": "application/json",
        ...buildGatewayHeaders(gateway),
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const response = await api.get(`/api/v1/user/agents/dto`);
    return response?.data ?? Promise.reject("Failed to fetch login DTO");
  } catch (error: any) {
    console.error("Error fetching login DTO:", error);
    throw new Error(error?.message || String(error));
  }
}

export interface LoginPasswordRequest {
  phone?: string;
  password?: string;
  /** Admission flow only — not sent for mobile-number login */
  admissionNumber?: string;
  countryCode?: string;
}

export interface LoginOtpRequest {
  phone?: string;
  otp?: string;
  /** Admission flow only — not sent for mobile-number login */
  admissionNumber?: string;
  countryCode?: string;
  page?: string;
}

interface LoginResponse {
  token?: string;
  message?: string;
  [key: string]: any;
}

export async function login(
  obj: LoginPasswordRequest,
  gateway: GatewayConfiguration
): Promise<LoginResponse> {
  try {
    const api = axios.create({
      baseURL: ApiUrlConstant.gateWayUrl,
      headers: buildGatewayHeaders(gateway),
    });

    const body: any = {
      ...(obj.countryCode && { isd_code: obj.countryCode }),
      ...(obj.phone && { phone: obj.phone.toString() }),
      ...(obj.admissionNumber && { admission_number: obj.admissionNumber }),
      ...(obj.password && { password: obj.password }),
    };

    const response = await api.post(`/api/v1/user/agents/login`, body);
    return response.data;
  } catch (error: any) {
    console.error("Login failed:", error);
    throw error?.response?.data || error?.message;
  }
}

export async function verifyAdmissionNumber(
  admissionNo: string,
  gateway: GatewayConfiguration
) {
  try {
    const api = axios.create({
      baseURL: ApiUrlConstant.gateWayUrl,
      headers: {
        "Content-Type": "application/json",
        ...buildGatewayHeaders(gateway),
      },
    });

    const response = await api.post(`/api/v1/user/agents/verify`, {
      admission_number: admissionNo,
    });

    return response?.data ?? Promise.reject("Failed to verify admission number");
  } catch (error: any) {
    console.error("Error verifying admission number:", error);
    throw new Error(error?.message || String(error));
  }
}

export async function verifyPhoneNumber(
  phoneData: {
    dialCode: string;
    number: string;
  },
  gateway: GatewayConfiguration
) {
  try {
    const api = axios.create({
      baseURL: ApiUrlConstant.gateWayUrl,
      headers: {
        "Content-Type": "application/json",
        ...buildGatewayHeaders(gateway),
      },
    });

    const response = await api.post(`/api/v1/user/agents/verify`, {
      isd_code: phoneData.dialCode,
      phone: phoneData.number.toString(),
    });

    return response?.data ?? Promise.reject("Failed to verify phone number");
  } catch (error: any) {
    console.error("Error verifying phone number:", error);
    throw new Error(error?.message || String(error));
  }
}

export async function generateOtp(
  obj: any,
  gateway: GatewayConfiguration
) {
  try {
    const url = `/api/v3/generate-otp`;
    const api = axios.create({
      baseURL: ApiUrlConstant.commServiceUrl,
      headers: {
        "Content-Type": "application/json",
      },
    });
    const body: any = {
      isd_code: obj?.dialCode?.toString(),
      phone: obj?.number?.toString(),
      tenant_id: gateway.tenantId,
      product_id: gateway.productId,
      ...(obj.firstName && { firstName: obj.firstName }),
      ...(obj.lastName && { lastName: obj.lastName }),
      ...(obj.gradeId && { gradeId: obj.gradeId }),
      ...(obj.admissionNumber && { admission_number: obj.admissionNumber }),
      ...(obj?.captcha_token && { captcha_token: obj.captcha_token }),
    };

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (obj?.register) {
      headers["X-Page"] = "REGISTER_GENERATE";
      headers["X-Platform"] = "web";
      headers["X-Product"] = "1";
    }

    const response = await api.post(url, body, { headers });

    return response?.data ?? Promise.reject("Failed to generate OTP");
  } catch (error: any) {
    console.error("Error generating OTP:", error);
    throw new Error(error?.message || String(error));
  }
}

export async function validateOtp(
  obj: LoginOtpRequest,
  gateway: GatewayConfiguration
) {
  try {
    const api = axios.create({
      baseURL: ApiUrlConstant.gateWayUrl,
      headers: {
        "Content-Type": "application/json",
        ...buildGatewayHeaders(gateway),
      },
    });

    const body = {
      ...(obj.countryCode && { isd_code: obj?.countryCode }),
      ...(obj.phone && { phone: obj?.phone?.toString() }),
      ...(obj.otp && { otp: obj?.otp?.toString() }),
      ...(obj.admissionNumber && { admission_number: obj?.admissionNumber }),
      product_id: String(gateway.productId),
      tenant_id: String(gateway.tenantId),
    };

    const response = await api.post(`/api/v1/user/agents/login-with-otp`, body);

    return response?.data ?? Promise.reject("Failed to validate OTP");
  } catch (error: any) {
    console.error("Error validating OTP:", error);

    if (error?.response?.data?.detail)
      throw new Error(error.response.data.detail);
    throw new Error(error?.message || String(error));
  }
}
